import type { ReactNode } from "react";
import { Field } from "./field";
import { SelectField } from "./select-field";
import { Button } from "./primitives";

/**
 * The transport details an e-way bill needs, captured at the loading bay (`011` FR-001, R2, R3).
 *
 * ## Mobile-first inside a desktop-first app
 *
 * `apps/admin` is desktop-first by `001`'s recorded decision, and that decision does not transfer
 * here. A Dispatcher is at a loading bay, on a phone, one-handed, possibly in gloves, in poor light.
 * So: one column at every width, 48px controls, 16px text (which is also what stops iOS zooming the
 * page on focus), no hover-dependent affordance, and a full-width submit at the bottom of the
 * thumb's reach. This is the warehouse touch standard from constitution IV, applied to the same
 * physical context minus the offline requirement.
 *
 * ## It implements no validation rules of its own (FR-009, R3)
 *
 * No vehicle-number format, no pin-code rule, no distance range — not as a regex, not as a
 * `pattern`, not as a `maxLength`. Vehicle-number formats vary by state and change; pin-code and
 * distance rules belong to the e-way bill portal. A helpful client-side rule here will be wrong
 * somewhere, and it will be wrong at a loading bay, where the person cannot investigate and the
 * truck cannot wait. `required` marks what must be present — which is FR-002's "refused with the
 * missing fields named", done by the browser — and the API's messages arrive back in `errors`.
 *
 * Modes of transport come from the API in `modes`. Not a list written here: this component states
 * no vocabulary of its own (FR-024).
 *
 * ## Captured details survive a failed attempt
 *
 * `values` re-fills every field. FR-004 and SC-002: a Dispatcher does not re-key seven fields
 * because the portal was busy.
 *
 * ## Fields the API decides
 *
 * `locked` names fields shown read-only with a line saying where the value comes from — the two
 * pincodes, which Billing sets itself (owner decision, 2026-10-26) and ignores if sent.
 */
export type TransportField =
  | "transporterName"
  | "transporterId"
  | "vehicleNumber"
  | "modeOfTransport"
  | "distanceKm"
  | "fromPinCode"
  | "toPinCode";

const LABEL: Record<TransportField, string> = {
  transporterName: "Transporter name",
  transporterId: "Transporter ID",
  vehicleNumber: "Vehicle number",
  modeOfTransport: "Mode of transport",
  distanceKm: "Distance (km)",
  fromPinCode: "From PIN code",
  toPinCode: "To PIN code",
};

/** 48px targets and 16px text on every control (constitution IV; 16px also stops iOS zooming). */
const TOUCH = "h-12 text-base";

export function TransportDetailsForm({
  action,
  modes,
  values = {},
  errors = {},
  submitLabel = "Generate e-way bill",
  locked = {},
  children,
}: {
  action: React.ComponentProps<"form">["action"];
  /** The modes the API offers, in its own words. Never a list invented here. */
  modes: readonly { value: string; label: string }[];
  /** What was already captured. Re-filled after a failed attempt (FR-004). */
  values?: Partial<Record<TransportField, string>>;
  /** The API's messages, per field, verbatim (FR-009, constitution VI). */
  errors?: Partial<Record<TransportField, string>>;
  submitLabel?: string;
  /** Fields the API fills, shown read-only, each with where its value comes from. */
  locked?: Partial<Record<Exclude<TransportField, "modeOfTransport">, ReactNode>>;
  /** A form-level failure surface, or the vehicle history. Rendered above the submit. */
  children?: ReactNode;
}) {
  const text = (name: Exclude<TransportField, "modeOfTransport">, extra?: React.ComponentProps<"input">) =>
    locked[name] !== undefined ? (
      // Keyed on the value: a defaultValue is read once, and the API's figure is the only one shown.
      <Field
        key={`${name}:${values[name] ?? ""}`}
        label={LABEL[name]}
        name={name}
        defaultValue={values[name]}
        error={errors[name]}
        className={`${TOUCH} bg-gray-100`}
        readOnly
        hint={locked[name]}
      />
    ) : (
      <Field
        label={LABEL[name]}
        name={name}
        defaultValue={values[name]}
        error={errors[name]}
        className={TOUCH}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        {...extra}
      />
    );

  return (
    <form
      action={action}
      data-slot="transport-details-form"
      // One column at every width. A two-column form at a loading bay is a form filled in wrongly.
      className="flex w-full max-w-md flex-col gap-5"
    >
      {text("transporterName", { autoCapitalize: "words", required: true })}
      {text("transporterId", { required: true })}
      {text("vehicleNumber", { required: true })}

      <SelectField
        label={LABEL.modeOfTransport}
        name="modeOfTransport"
        defaultValue={values.modeOfTransport}
        error={errors.modeOfTransport}
        className={TOUCH}
        required
      >
        {modes.map((mode) => (
          <option key={mode.value} value={mode.value}>
            {mode.label}
          </option>
        ))}
      </SelectField>

      {text("distanceKm", { type: "number", inputMode: "decimal", min: "0", required: true })}
      {text("fromPinCode", { inputMode: "numeric", required: true })}
      {text("toPinCode", { inputMode: "numeric", required: true })}

      {children}

      <Button type="submit" size="lg" className="h-12 w-full text-base">
        {submitLabel}
      </Button>
    </form>
  );
}
